import { fromJsonString, toJsonString } from "@bufbuild/protobuf";
import { Code } from "@connectrpc/connect";
import { useQuery } from "@connectrpc/connect-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { useEffect } from "react";

import { AuthService, StaffRole, StaffSessionSchema, type GetStaffSessionResponse, type StaffSession } from "@yatayat/gen/yatayat/auth/v1/auth_pb";

import { errorCode, retryTransient } from "~/lib/rpc/errors";

export type StaffSessionState = {
  /** Unset when no one is signed in, and while the first answer is pending. */
  session: StaffSession | undefined;
  isPending: boolean;
  isError: boolean;
};

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const rememberedKey = "yatayat.staff.session";

// What a failed fetch or a dropped connection looks like, as opposed to an answer from the api.
const unreachable = new Set([Code.Unknown, Code.Unavailable, Code.DeadlineExceeded, Code.Canceled]);

function defaultStorage(): StorageLike | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** Keeps the session the server confirmed, or forgets it when there is none. */
export function rememberStaffSession(session: StaffSession | undefined, storage = defaultStorage()) {
  try {
    if (session) storage?.setItem(rememberedKey, toJsonString(StaffSessionSchema, { ...session, phoneMasked: "" }));
    else storage?.removeItem(rememberedKey);
  } catch {
    // A full or blocked storage only costs the offline fallback.
  }
}

/** The last session the server confirmed on this device, if any. */
export function rememberedStaffSession(storage = defaultStorage()): StaffSession | undefined {
  try {
    const raw = storage?.getItem(rememberedKey);
    return raw ? fromJsonString(StaffSessionSchema, raw, { ignoreUnknownFields: true }) : undefined;
  } catch {
    return undefined;
  }
}

type SessionQuery = Pick<UseQueryResult<GetStaffSessionResponse>, "data" | "isPending" | "isError" | "error" | "failureReason" | "fetchStatus">;

/**
 * The session to act on. When the api can't be reached and hasn't answered since the
 * page loaded, it is the last one the server confirmed on this device: the server
 * still checks the real session on every call, including the boarding sync.
 */
export function staffSessionState(query: SessionQuery, remembered: () => StaffSession | undefined): StaffSessionState {
  const cantReach =
    query.fetchStatus === "paused" || [query.error, query.failureReason].some((err) => err != null && unreachable.has(errorCode(err)));
  const fallback = !query.data && cantReach ? remembered() : undefined;
  if (fallback) return { session: fallback, isPending: false, isError: false };

  return { session: query.data?.session, isPending: query.isPending, isError: query.isError };
}

/**
 * The staff member's session. A conductor who reloads with no signal keeps the last
 * one this device saw, so the boarding scan still opens (ADR-0009).
 */
export function useStaffSession(): StaffSessionState {
  const query = useQuery(AuthService.method.getStaffSession, {}, { retry: retryTransient });
  const { data } = query;

  useEffect(() => {
    if (data) rememberStaffSession(data.session);
  }, [data]);

  return staffSessionState(query, rememberedStaffSession);
}

const operatorRoles = new Set([StaffRole.OPERATOR_ADMIN, StaffRole.COUNTER_CLERK, StaffRole.CONDUCTOR]);
const regulatorRoles = new Set([StaffRole.REGULATOR_ADMIN, StaffRole.PLANNER, StaffRole.INSPECTOR]);

/** What each role may do in the operator app; the server enforces the same rules. */
export const can = {
  useOperatorApp: (role: StaffRole) => operatorRoles.has(role),
  useConsole: (role: StaffRole) => regulatorRoles.has(role),
  draftFares: (role: StaffRole) => role === StaffRole.REGULATOR_ADMIN || role === StaffRole.PLANNER,
  // Approving and publishing also need a different person from the drafter.
  approveFares: (role: StaffRole) => role === StaffRole.REGULATOR_ADMIN,
  sell: (role: StaffRole) => role === StaffRole.OPERATOR_ADMIN || role === StaffRole.COUNTER_CLERK,
  board: (role: StaffRole) => role === StaffRole.OPERATOR_ADMIN || role === StaffRole.CONDUCTOR,
  viewReport: (role: StaffRole) => role === StaffRole.OPERATOR_ADMIN || role === StaffRole.COUNTER_CLERK,
  printStickers: (role: StaffRole) => role === StaffRole.OPERATOR_ADMIN,
  // Schedules, the fleet's papers and vehicle swaps.
  manage: (role: StaffRole) => role === StaffRole.OPERATOR_ADMIN,
};
